import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  type User as FirebaseUser,
  type Unsubscribe,
} from 'firebase/auth'
import { auth } from './config'

export type { FirebaseUser }

// Providers
const googleProvider = new GoogleAuthProvider()
googleProvider.addScope('email')
googleProvider.addScope('profile')

const facebookProvider = new FacebookAuthProvider()
facebookProvider.addScope('email')
facebookProvider.addScope('public_profile')

const appleProvider = new OAuthProvider('apple.com')
appleProvider.addScope('email')
appleProvider.addScope('name')

// Email/Password Auth
export async function signInWithEmail(email: string, password: string): Promise<FirebaseUser> {
  const result = await signInWithEmailAndPassword(auth, email, password)
  return result.user
}

export async function signUpWithEmail(email: string, password: string): Promise<FirebaseUser> {
  const result = await createUserWithEmailAndPassword(auth, email, password)
  return result.user
}

// Social Auth
export async function signInWithGoogle(): Promise<FirebaseUser> {
  const result = await signInWithPopup(auth, googleProvider)
  return result.user
}

export async function signInWithFacebook(): Promise<FirebaseUser> {
  const result = await signInWithPopup(auth, facebookProvider)
  return result.user
}

export async function signInWithApple(): Promise<FirebaseUser> {
  const result = await signInWithPopup(auth, appleProvider)
  return result.user
}

// Common Auth Functions
export async function signOut(): Promise<void> {
  await firebaseSignOut(auth)
}

export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email)
}

export async function getIdToken(user: FirebaseUser): Promise<string> {
  return user.getIdToken()
}

export function onAuthChange(callback: (user: FirebaseUser | null) => void): Unsubscribe {
  return onAuthStateChanged(auth, callback)
}

export function getCurrentUser(): FirebaseUser | null {
  return auth.currentUser
}

// Helper to extract user info from social providers
export function extractUserInfo(user: FirebaseUser): {
  email: string | null
  firstName: string | null
  lastName: string | null
} {
  const displayName = user.displayName || ''
  const nameParts = displayName.split(' ')

  return {
    email: user.email,
    firstName: nameParts[0] || null,
    lastName: nameParts.slice(1).join(' ') || null,
  }
}
